import TransformComponent from '../../../Component/Transform';
import { Easing } from '@tweenjs/tween.js';
import AnimationClip from '../AnimationClip';
import Interactable from '../../../Component/Interactable';
import CardSystem, { getCardHandler } from '../../Card/CardSystem';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import { PlaySound } from '../../Audio/AudioHelper';
import { AudioTrackType } from '../../Audio/AudioClip';
import {
    showCardScoreEffectAt,
    showCardCoinEffectAt,
} from '../CardAnimationHelper';
import Point from 'Utility/Point';
import { delay } from '../../../Utility/Task/TaskQueue';

export interface ICardMatchMetaData {
    system: CardSystem;
    cardScore: number;
    coinsCollected: number;
    playAudio?: boolean;
    forceScale?: boolean;
    target?: Point;
    isConnectedCard?: boolean;
    delay?: number;
}

export class CardMatch extends AnimationClip<ICardMatchMetaData> {
    public *execute(
        card: BaseCard,
        meta: ICardMatchMetaData
    ): IterableIterator<number> {
        if (meta.delay) {
            yield* delay(meta.delay);
        }

        const { playAudio = true, forceScale = true, target = Point.Zero } = meta;
        if (playAudio) {
            let streakAudioIndex = meta.system.store.getState().levelState
                .cardsState.matchSequence.length;
            if (streakAudioIndex > 10) {
                streakAudioIndex = 10;
            }

            PlaySound(
                [
                    'rising-tones1',
                    'rising-tones2',
                    'rising-tones3',
                    'rising-tones4',
                    'rising-tones5',
                    'rising-tones6',
                    'rising-tones7',
                    'rising-tones8',
                    'rising-tones9',
                    'rising-tones10',
                ][streakAudioIndex - 1]
            );
        }

        const handler = getCardHandler(card);

        if (!meta.isConnectedCard) {
            handler.setCardFaceUp(true);
            handler.setCardModifiersVisible(false);

            // Add to stack container maintaining previous global position.
            meta.system.stackContainer.addChild(card);
            meta.system.cardStack.sort();
        }

        const transform: TransformComponent = card.getComponent(
            TransformComponent
        );
        const interactable = card.getComponent(Interactable);
        interactable.enabled = false;

        if (meta.coinsCollected) {
            showCardCoinEffectAt(
                meta.system.scene,
                card.parent,
                meta.coinsCollected,
                transform.position.clone()
            );

            meta.system.scene.emit('UpdateScoreProgress');
        } else {
            showCardScoreEffectAt(
                meta.system.scene,
                card.parent,
                meta.cardScore,
                transform.position.clone()
            );
        }

        // Fix scale in case interrupting flip animation.
        if (forceScale) {
            transform.scale.copy(Point.One);
        }

        const arcYPosition = transform.y - 100;

        const tweenX = this.getTween(transform.position);
        const tweenYIN = this.getTween(transform.position);
        const tweenYOUT = this.getTween(transform.position);
        const tweenRotation = this.getTween(transform);

        tweenX.to({ x: target.x }, 1000);
        tweenX.easing(Easing.Quadratic.Out);

        tweenYIN.to({ y: arcYPosition }, 500);
        tweenYIN.easing(Easing.Quadratic.Out);

        tweenRotation.to({ rotation: Math.PI * 4 }, 1000);
        tweenRotation.easing(Easing.Cubic.Out);

        tweenX.start(this.elapsedTime);
        tweenRotation.start(this.elapsedTime);

        yield* this.startTween(tweenYIN);

        tweenYOUT.to({ y: target.y }, 500);
        tweenYOUT.easing(Easing.Quadratic.In);

        yield* this.startTween(tweenYOUT);
        transform.rotation = 0;
    }
}
